import fetchGql, { gql } from '../util/fetchGql';
import Seo, { SEO_FRAGMENT } from '../components/Seo';
import ProductContent, { PRODUCT_CONTENT_FRAGMENT } from '../components/ProductContent';
import { PRODUCT_CARD_FRAGMENT } from '../components/ProductCard';
import Offer, {
	OFFER_FRAGMENT,
	OFFER_MODALS_FRAGMENT
} from '../components/Offer';
import { useRouter } from 'next/router';
import ProductHero from '../components/ProductHero';
import Gallery from '../components/Gallery';
import RelatedProducts from '../components/RelatedProducts';
import { useEffect, useState } from 'react';

export default function Product ({
	id: productId, seo, productContent, offer, offerModals, relatedProducts, accentColour,
	galleryAppearance, offerPosition, variantsById, defaultVariantId,
}) {
	const id = process.browser ? +window.location.hash.replace('#variant_', '') : defaultVariantId;
	const [activeVariant, setActiveVariant] = useState(variantsById?.[!id || isNaN(id) ? defaultVariantId : id]);
	const defaultVariant = variantsById[defaultVariantId];

	useEffect(() => {
		setActiveVariant(variantsById?.[!id || isNaN(id) ? defaultVariantId : id]);
	}, [defaultVariantId]);

	if (useRouter().isFallback)
		return null;

	const onVariantChange = id => setActiveVariant(variantsById[id]);

	return (
		<>
			<Seo seo={seo} />
			<ProductHero key={productId}>
				<Gallery
					key={id}
					images={(activeVariant?.images?.length ?? 0) > 0 ? activeVariant?.images : defaultVariant?.images}
					shrink={offerPosition !== 'onTopLeft'}
					stacked={galleryAppearance === 'stacked'}
					childrenLeft={offerPosition === 'left'}
				>
					<Offer
						offerModals={offerModals}
						float={offerPosition === 'onTopLeft'}
						accent={accentColour}
						{...offer}
						activeVariant={activeVariant}
						onVariantChange={onVariantChange}
					/>
				</Gallery>
			</ProductHero>
			<ProductContent
				blocks={productContent}
				accent={accentColour}
			/>
			<RelatedProducts products={relatedProducts} />
		</>
	);
}

export async function getStaticProps ({ params: { slug }, ctx }) {
	const data = await fetchGql(gql`
        query GetProduct ($slug: String!) {
            product (slug: [$slug]) {
                ... on products_Product {
	                id
                    seo { ...Seo }
                    accentColour
                    galleryAppearance
                    offerPosition
                    productContent {
                        ...ProductContent
                    }
                    relatedProducts {
                        ...ProductCard
                    }
                    variants {
                        ... on products_Variant {
                            id
                            isDefault
                            price: priceAsCurrency
                            sale: salePriceAsCurrency
                            rrp
                            images {
                                id
                                url @transform(
                                    width: 1720
                                    height: 920
	                                mode: "fit"
                                )
                            }
                        }
                    }
                }
            }
            offer: product (slug: [$slug]) {
                ...Offer
            }
            ...OfferModals
        }
        ${SEO_FRAGMENT}
        ${PRODUCT_CONTENT_FRAGMENT}
        ${PRODUCT_CARD_FRAGMENT}
        ${OFFER_FRAGMENT}
        ${OFFER_MODALS_FRAGMENT}
	`, {
		slug,
	}, ctx);

	if (!data?.product)
		return { notFound: true };

	let defaultVariantId = null;

	const variantsById = data.product.variants.reduce((a, b) => {
		a[b.id] = b;

		if (b.isDefault) defaultVariantId = b.id;

		return a;
	}, {});

	return {
		props: {
			...data.product,
			offer: data.offer,
			offerModals: data.offerModals,
			variantsById,
			defaultVariantId,
		},
	};
}

export async function getStaticPaths () {
	// const data = await fetchGql(gql`{
    //     products (
	//         isHidden: false
	//         limit: 1
    //     ) {
    //         slug
    //     }
    // }`);

	return {
		paths: [],//data.products.map(e => ({ params: e })),
		fallback: 'blocking',
	};
}
